/** *********************************************
 *
 * CAROUSEL
 *
 * example usage:
 *
 *  const testCarousel = new Carousel('.js-example-carousel-selector');
 *  testCarousel.onScrollAnalytics(() => {
 *    // ga
 *  });
 *  testCarousel.onRightClickAnalytics(() => {
 *    // ga
 *  });
 *  testCarousel.onLeftClickAnalytics(() => {
 *    // ga
 *  });
 *  testCarousel.initiate();
 *
 ********************************************* */

/* global common */
import { byWindowAction, debounce } from '@html/_common/utils';

export default class AOCarousel {
  constructor(
    carouselSelector,
    options = {},
  ) {
    // selector
    this.carouselSelector = carouselSelector;

    // options
    this.scrollMultiple = options.scrollMultiple;

    // analytics callbacks
    this.onScrollAnalyticsCallback = null;
    this.onRightClickAnalyticsCallback = null;
    this.onLeftClickAnalyticsCallback = null;

    // elements
    this.$carousel = null;
    this.$itemsContainer = null;
    this.$arrowLeft = null;
    this.$arrowRight = null;

    // helper
    this.index = 0; // index of left-most element shown (Not used in touch mode)
    this.common = typeof byWindowAction === 'undefined' ? common : { func: { debounce }, byWindowAction };
  }

  /** ********************************************
   *
   * PUBLIC
   *
   ********************************************* */

  initiate(callback) {
    this.queryElements();
    this.attachEventHandlers();
    this.scrollToCurrentIndex();
    this.checkArrowVisibility();
    if (callback && typeof callback === 'function') callback();
  }

  onScrollAnalytics(callback) {
    this.onScrollAnalyticsCallback = callback;
  }

  onRightClickAnalytics(callback) {
    this.onRightClickAnalyticsCallback = callback;
  }

  onLeftClickAnalytics(callback) {
    this.onLeftClickAnalyticsCallback = callback;
  }

  /** ********************************************
   *
   * PRIVATE
   *
   ********************************************* */

  queryElements() {
    this.$carousel = $(this.carouselSelector);
    this.$itemsContainer = this.$carousel.find('.js-ao-common-carousel__items-container');
    this.$arrowLeft = this.$carousel.find('.js-ao-common-carousel__arrow--left');
    this.$arrowRight = this.$carousel.find('.js-ao-common-carousel__arrow--right');
  }

  attachEventHandlers() {
    this.$arrowLeft.click(() => {
      this.onClickArrow('left');
    });

    this.$arrowRight.click(() => {
      this.onClickArrow('right');
    });

    if (this.onScrollAnalyticsCallback) {
      this.$itemsContainer.on('scroll', this.common.func.debounce(this.onScroll.bind(this), 300));
    }

    this.common.byWindowAction(['resize'], this.common.func.debounce(this.onResize.bind(this), 300));
  }

  onClickArrow(direction) {
    this.calculateNewIndex(direction);

    this.onArrowClickAnalytics(direction);

    this.scrollToCurrentIndex();

    this.checkArrowVisibility();
  }

  calculateNewIndex(direction) {
    const numberOfElementsShown = this.calcNumberOfElementsShown();
    const totalNumberOfElements = this.getNumberOfElements();
    const currentMath = this.index >= totalNumberOfElements - numberOfElementsShown
      ? Math.max
      : Math.min;

    if (direction === 'right') {
      if (this.scrollMultiple) {
        this.index = currentMath(
          this.index + numberOfElementsShown,
          totalNumberOfElements - numberOfElementsShown,
        );
      } else {
        this.index = currentMath(this.index + 1, totalNumberOfElements - numberOfElementsShown);
      }
    }

    if (direction === 'left') {
      if (this.scrollMultiple) {
        // if we are at last index & remainder is not 0. That means on last scroll right, we only moved
        // the remainder => therefore for UX we only scroll back the remainder
        let numberOfElementsToScroll = numberOfElementsShown;
        const remainder = totalNumberOfElements % numberOfElementsShown;
        if (remainder !== 0 && this.index === totalNumberOfElements - numberOfElementsShown) {
          numberOfElementsToScroll = remainder;
        }
        this.index = Math.max(0, this.index - numberOfElementsToScroll);
      } else {
        this.index = Math.max(0, this.index - 1);
      }
    }
  }

  scrollToCurrentIndex() {
    const widthOfSingleElement = this.getWidthOfSingleElement();
    const marginLeft = -(widthOfSingleElement * this.index);
    const $firstElement = this.getFirstElement();
    $firstElement.css('margin-left', marginLeft);
  }

  checkArrowVisibility() {
    const numberOfElements = this.getNumberOfElements();
    const widthOfSingleElement = this.getWidthOfSingleElement();
    const numberOfElementsShown = this.calcNumberOfElementsShown();

    const allowArrows = this.$itemsContainer.width() < numberOfElements * widthOfSingleElement;

    const showLeftArrow = allowArrows && this.index !== 0;
    const showRightArrow = allowArrows && this.index < numberOfElements - numberOfElementsShown;

    this.$arrowLeft.toggleClass('hid', !showLeftArrow);
    this.$arrowRight.toggleClass('hid', !showRightArrow);
  }

  onScroll() {
    this.onScrollAnalyticsCallback();
  }

  onResize() {
    // reset carousel
    this.index = 0;
    this.scrollToCurrentIndex(this.index);

    // check for arrow visbility
    this.checkArrowVisibility();
  }

  onArrowClickAnalytics(direction) {
    if (direction === 'right' && this.onRightClickAnalyticsCallback) {
      this.onRightClickAnalyticsCallback();
    }
    if (direction === 'left' && this.onLeftClickAnalyticsCallback) {
      this.onLeftClickAnalyticsCallback();
    }
  }

  calcNumberOfElementsShown() {
    return Math.round(this.$itemsContainer.width() / this.getWidthOfSingleElement());
  }

  getWidthOfSingleElement() {
    // eslint-disable-next-line
    const gapNumber = isNaN(this.$itemsContainer.css('gap')) ? 0 : parseInt(this.$itemsContainer.css('gap'), 10);
    return parseInt(this.getFirstElement().width(), 10) + gapNumber;
  }

  getFirstElement() {
    return this.getElements().first();
  }

  getNumberOfElements() {
    return this.getElements().length;
  }

  getElements() {
    return this.$itemsContainer.children();
  }
}
